/**
 * 검색 목록의 지점 — **중복 없는 지점표 + 상품별 번호**.
 *
 * 상품-지점은 다대다라, 상품마다 지점 상세를 붙이면 같은 지점이 그 지점을 파는 상품
 * 수만큼 반복된다. dev 실측(2026-09-23, 승인 320건): 지점 부착 11,771건 vs 실제
 * 지점 ~382곳 — Bestbike 차종 상품 91개가 같은 지점 113~122곳을 각자 들고 있었다.
 * 그 반복이 검색 결과를 2MB 넘게 키워 Next 데이터 캐시(항목당 2MB)에 저장되지
 * 못했고, 브라우저로도 그대로 전송됐다.
 *
 * 그래서 조회(`get_product_branch_index` RPC)부터 지점 상세는 한 번씩만 받고,
 * 캐시·브라우저까지 **이 모양 그대로** 넘긴다. 번호를 지점으로 바꾸는 건 화면
 * 직전(`hydrateProductBranches`, 클라이언트)에서 한 번뿐이다 — 그때도 같은 지점
 * 객체를 참조로 공유하므로 복제되지 않는다.
 *
 * 변환 규칙은 기존 경로(`branch-summaries` 합성 행 → `transformers`)와 **같다**:
 *   - 핀(branches): 좌표가 숫자이고 0이 아닌 지점만, city = city || "",
 *     country = country || null
 *   - 카드 도시(branch_locations): 좌표와 무관하게 모든 지점의 city 중 빈 값 제외,
 *     대소문자 무시 중복 제거(처음 표기 유지)
 *   - allowedBranchIds 가 있으면 둘 다 그 지점만(지도 범위 검색)
 */

import type { Product } from "@/types";

/**
 * 검색 결과 한 벌 — 상품(번호만) + 그 결과의 지점표.
 *
 * 둘은 **항상 함께** 다닌다. 번호는 이 결과의 지점표에서만 뜻이 있어서, 다른 결과의
 * 표와 섞으면 엉뚱한 지점을 가리킨다.
 */
export interface SearchListResult {
  products: Product[];
  branchTable: SearchBranch[];
}

export const EMPTY_SEARCH_RESULT: SearchListResult = {
  products: [],
  branchTable: [],
};

/** get_product_branch_index 가 돌려주는 지점 1행(지점당 한 번). */
export interface BranchIndexRow {
  id: string;
  latitude: number | string | null;
  longitude: number | string | null;
  country: string | null;
  city: string | null;
}

/** get_product_branch_index 반환값. `idx` 는 `branches` 의 0-base 번호. */
export interface BranchIndexPayload {
  branches: BranchIndexRow[];
  products: Array<{ product_id: string; idx: number[] }>;
}

/** 지도 핀 하나 — 기존 `Product.branches` 원소와 같은 모양. */
export interface SearchBranch {
  id: string;
  city: string;
  latitude: number;
  longitude: number;
  country: string | null;
}

export interface ProductBranchRefs {
  /** 지점표(`table`)의 번호 — 좌표가 있는(핀이 되는) 지점만 */
  branchIdx: number[];
  /** 카드의 도시 표기 — 좌표 없는 지점 포함 */
  branchLocations: string[];
}

export interface SearchBranchIndex {
  /** 핀으로 쓰이는 지점만, 지점당 한 번 */
  table: SearchBranch[];
  byProduct: Map<string, ProductBranchRefs>;
}

/** 숫자로 못 읽히는 좌표(null/빈 문자열)는 null — branch-summaries.toCoordinate 와 같다. */
function toCoordinate(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function buildSearchBranchIndex(
  payload: BranchIndexPayload,
  allowedBranchIds?: readonly string[],
): SearchBranchIndex {
  const allowed = allowedBranchIds ? new Set(allowedBranchIds) : null;

  // 원 번호 → 핀 지점(좌표가 있고 허용된 것만). 기존 변환은 `latitude ? … : null`
  // 이라 0 도 핀에서 뺀다 — 같은 규칙을 지킨다.
  const pinOf = (i: number): SearchBranch | null => {
    const r = payload.branches[i];
    if (!r?.id) return null;
    if (allowed && !allowed.has(r.id)) return null;
    const latitude = toCoordinate(r.latitude);
    const longitude = toCoordinate(r.longitude);
    if (!latitude || !longitude) return null;
    return {
      id: r.id,
      city: r.city || "",
      latitude,
      longitude,
      country: r.country || null,
    };
  };

  const table: SearchBranch[] = [];
  const compactIndexOf = new Map<number, number>();
  const tableIndex = (i: number): number | null => {
    const known = compactIndexOf.get(i);
    if (known !== undefined) return known;
    const pin = pinOf(i);
    if (!pin) return null;
    table.push(pin);
    compactIndexOf.set(i, table.length - 1);
    return table.length - 1;
  };

  const byProduct = new Map<string, ProductBranchRefs>();
  for (const { product_id, idx } of payload.products) {
    const branchIdx: number[] = [];
    const cities = new Map<string, string>();

    for (const i of idx) {
      const r = payload.branches[i];
      if (!r?.id) continue;
      if (allowed && !allowed.has(r.id)) continue;

      const city = r.city || "";
      if (city && !cities.has(city.toLowerCase())) {
        cities.set(city.toLowerCase(), city);
      }

      const t = tableIndex(i);
      if (t !== null) branchIdx.push(t);
    }

    byProduct.set(product_id, {
      branchIdx,
      branchLocations: [...cities.values()],
    });
  }

  return { table, byProduct };
}

/**
 * 화면 직전 — 번호를 지점으로 바꿔 기존 `branches` 모양을 되돌린다.
 *
 * 지점 객체는 상품끼리 **참조로 공유**한다(복제 없음). 소비자(지도 마커·재고 확인·
 * 차종 시트)는 읽기만 한다.
 */
export function hydrateProductBranches<T extends { branchIdx?: number[] }>(
  products: T[],
  table: readonly SearchBranch[],
): Array<T & { branches: SearchBranch[] }> {
  return products.map((product) => ({
    ...product,
    branches: (product.branchIdx ?? [])
      .map((i) => table[i])
      .filter((b): b is SearchBranch => b !== undefined),
  }));
}

/**
 * get_product_branch_index 호출. 생성된 DB 타입이 이 RPC 를 아직 몰라서, 호출부마다
 * 캐스팅하는 대신 여기서 한 번 구조적 타입으로 좁힌다(branch-summaries 와 같은 방식).
 */
interface BranchIndexRpcClient {
  rpc(
    fn: "get_product_branch_index",
    args: { p_product_ids: string[]; p_locale: string },
  ): PromiseLike<{
    data: BranchIndexPayload | null;
    error: { message: string } | null;
  }>;
}

/**
 * 상품 id 들의 지점을 **한 번의 호출**로 받는다.
 *
 * 청크로 나누지 않는다 — 나누면 청크마다 지점표가 따로 생겨 번호를 다시 매겨야 한다.
 * 요청 본문은 UUID 600개여도 ~22KB 이고, 응답은 지점당 한 번이라 상품 수에 비례해
 * 커지지 않는다(dev 320건 ≈ 101KB, 기존 RPC 546KB).
 *
 * 실패는 **삼키지 않는다** — 호출부가 unstable_cache 안이라 빈 값을 돌려주면
 * "지점 없는 목록"이 캐시에 굳는다. throw 는 캐시되지 않는다.
 */
export async function fetchBranchIndex(
  client: unknown,
  productIds: string[],
  locale: string,
): Promise<BranchIndexPayload> {
  const unique = [...new Set(productIds.filter(Boolean))];
  if (unique.length === 0) return { branches: [], products: [] };

  const { data, error } = await (client as BranchIndexRpcClient).rpc(
    "get_product_branch_index",
    { p_product_ids: unique, p_locale: locale },
  );
  if (error) {
    throw new Error(`get_product_branch_index failed: ${error.message}`);
  }
  return data ?? { branches: [], products: [] };
}

/**
 * 변환이 끝난 상품에 지점 번호·카드 도시를 붙이고, 결과 한 벌로 묶는다.
 *
 * 상품 행은 `branch_products` 없이 변환되므로 `branches`/`branch_locations` 가 비어
 * 있다 — 여기서 채운다. `branches` 는 **보내지 않는다**(화면 직전에 번호로 복원).
 */
export function toSearchListResult(
  products: Product[],
  index: SearchBranchIndex,
): SearchListResult {
  return {
    products: products.map((product) => {
      const refs = index.byProduct.get(String(product.id));
      const { branches: _omit, ...rest } = product;
      void _omit;
      return {
        ...rest,
        branchIdx: refs?.branchIdx ?? [],
        branch_locations: refs?.branchLocations ?? [],
      };
    }),
    branchTable: index.table,
  };
}

/**
 * 이미 `branches` 가 붙어 나온 결과(현재 위치 검색 = 카드 경로, 홈과 공유)를 같은
 * 결과 모양으로 맞춘다. 이 경로는 조회를 바꾸지 않았다 — 반경 안 상품 100개 이내라
 * 반복이 작고, 쿼리를 홈 화면과 함께 쓰기 때문이다. 캐시·브라우저로는 번호만 보낸다.
 *
 * 같은 지점은 **내용까지 같을 때만** 한 칸을 공유한다(id 만 같고 도시 표기가 다르면
 * 따로 둔다 — 한쪽으로 덮으면 다른 상품의 표기가 바뀐다).
 */
export function searchListResultFromProducts(products: Product[]): SearchListResult {
  const table: SearchBranch[] = [];
  const indexByContent = new Map<string, number>();

  const indexOf = (branch: SearchBranch): number => {
    const key = JSON.stringify([
      branch.id,
      branch.city,
      branch.latitude,
      branch.longitude,
      branch.country ?? null,
    ]);
    let index = indexByContent.get(key);
    if (index === undefined) {
      index = table.length;
      table.push({ ...branch, country: branch.country ?? null });
      indexByContent.set(key, index);
    }
    return index;
  };

  return {
    products: products.map((product) => {
      const { branches, ...rest } = product;
      return {
        ...rest,
        branchIdx: (branches ?? []).map((b) =>
          indexOf({ ...b, country: b.country ?? null }),
        ),
      };
    }),
    branchTable: table,
  };
}
